// Uploading photos and videos from the walk page (2026-10-04). The browser does the work the
// server can't afford on a 512 MB machine: it makes the two JPEG previews (for a video, from a
// frame of it), reads when and where the file was taken, and PUTs everything straight to the
// bucket with the presigned links from POST /api/walks/:id/photos/uploads. Then
// POST /api/walks/:id/photos adds it to the walk.
import { postJSON } from './api.js'
import { ukInputToISO } from './format.js'

// The same sizes as scripts/postgres_import/upload_photos.py makes for the MapMyWalk photos.
const PREVIEWS = { thumb: 480, display: 1600 }
const JPEG_QUALITY = 0.82

// Some browsers leave File.type empty for less common formats; go by the extension then.
const BY_EXTENSION = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif', avif: 'image/avif',
  mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm', '3gp': 'video/3gpp', mkv: 'video/x-matroska',
}
export const ACCEPT = 'image/*,video/*'

export function contentTypeOf(file) {
  if (file.type) return file.type
  return BY_EXTENSION[file.name.split('.').pop().toLowerCase()] ?? ''
}

// Draws source (an ImageBitmap or a <video>) into a JPEG no bigger than longEdge, never enlarged.
function jpegFrom(source, width, height, longEdge) {
  const scale = Math.min(1, longEdge / Math.max(width, height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width * scale))
  canvas.height = Math.max(1, Math.round(height * scale))
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('could not make a preview'))), 'image/jpeg', JPEG_QUALITY)
  })
}

async function previewsFrom(source, width, height) {
  const out = {}
  for (const [name, edge] of Object.entries(PREVIEWS)) out[name] = await jpegFrom(source, width, height, edge)
  return out
}

// A photo: previews in its upright orientation (createImageBitmap applies the EXIF rotation), and
// the EXIF time and position when present.
async function preparePhoto(file) {
  let bitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    throw new Error("this browser can't open this image")
  }
  const info = { width: bitmap.width, height: bitmap.height, previews: await previewsFrom(bitmap, bitmap.width, bitmap.height) }
  bitmap.close()
  return { ...info, ...(await readPhotoMetadata(file)) }
}

// A photo's EXIF time and position, when present (else {}: the photo is still added, just
// without a time or place).
async function readPhotoMetadata(file) {
  const out = {}
  try {
    const { default: exifr } = await import('exifr')
    const tags = await exifr.parse(file, { tiff: true, exif: true, gps: true, xmp: false, icc: false, iptc: false, jfif: false })
    const taken = exifTime(tags?.DateTimeOriginal ?? tags?.CreateDate, tags?.OffsetTimeOriginal)
    if (taken) out.takenAt = taken
    if (Number.isFinite(tags?.latitude) && Number.isFinite(tags?.longitude)) {
      out.latitude = tags.latitude
      out.longitude = tags.longitude
    }
  } catch {
    // No readable EXIF.
  }
  return out
}

// The same check as the server's plausibleTakenAt: a reset camera clock (1980, 1 Jan 2000) or a
// future time isn't a real taken time, and the server stores none for it.
const EARLIEST_TAKEN_AT = Date.UTC(2000, 0, 2)
export const plausibleTakenAt = (iso) => {
  const t = Date.parse(iso)
  return t >= EARLIEST_TAKEN_AT && t <= Date.now() + 24 * 3600 * 1000
}

// Just when a file was taken (ISO, or null), read the way the upload reads it, without making
// previews: the photos page's bulk upload uses it to pick each file's walk before uploading.
export async function readTakenAt(file) {
  const contentType = contentTypeOf(file)
  let takenAt = null
  if (contentType.startsWith('image/')) takenAt = (await readPhotoMetadata(file)).takenAt
  else if (contentType.startsWith('video/')) takenAt = (await readMp4Metadata(file).catch(() => ({}))).takenAt
  return takenAt && plausibleTakenAt(takenAt) ? takenAt : null
}

// EXIF times are wall-clock readings with no zone unless OffsetTimeOriginal gives one. exifr
// returns them as a Date in the browser's zone, so the wall-clock fields are read back out of it;
// with no offset they're taken as UK time (BST or GMT by the date, as the rest of the site does),
// so an upload from a browser in another zone, or a computer set to UTC, still lands right. A
// time in the hour repeated when the clocks go back is taken as the GMT one.
function exifTime(date, offset) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null
  const pad = (n) => String(n).padStart(2, '0')
  const local = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  const m = typeof offset === 'string' && offset.match(/^([+-])(\d{2}):(\d{2})$/)
  if (!m) return ukInputToISO(local)
  return new Date(`${local}${offset}`).toISOString()
}

// A video: size and length from a <video> element, previews from a frame a second in (or a tenth
// of the way through a short clip), and time and place from the MP4/QuickTime header. If the
// browser can't play the codec (e.g. HEVC in Firefox), it's still uploaded, without previews.
async function prepareVideo(file) {
  // The header's values stand when the browser can't decode the video; its own win otherwise.
  const info = { previews: null, ...(await readMp4Metadata(file).catch(() => ({}))) }
  const url = URL.createObjectURL(file)
  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.preload = 'auto'
  try {
    await waitFor(video, 'loadedmetadata', () => { video.src = url })
    if (Number.isFinite(video.duration)) info.durationMs = Math.round(video.duration * 1000)
    if (video.videoWidth > 0) {
      info.width = video.videoWidth
      info.height = video.videoHeight
      await waitFor(video, 'seeked', () => { video.currentTime = Math.min(1, (video.duration || 0) / 10) })
      info.previews = await previewsFrom(video, video.videoWidth, video.videoHeight)
    }
  } catch {
    // Can't decode it here: no previews.
  } finally {
    video.removeAttribute('src')
    video.load()
    URL.revokeObjectURL(url)
  }
  return info
}

function waitFor(el, event, start, timeoutMs = 20000) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => { cleanup(); reject(new Error(`${event} timed out`)) }, timeoutMs)
    const ok = () => { cleanup(); resolve() }
    const fail = () => { cleanup(); reject(new Error('cannot decode')) }
    const cleanup = () => {
      clearTimeout(timer)
      el.removeEventListener(event, ok)
      el.removeEventListener('error', fail)
    }
    el.addEventListener(event, ok)
    el.addEventListener('error', fail)
    start()
  })
}

// Reads moov/mvhd's creation time (seconds since 1904, UTC; 0 = not set) and the position in
// moov/udta/©xyz ("+53.7874-001.8857/", written by Android and Apple cameras) without loading the
// whole file: only the box headers and the moov box (normally well under a megabyte) are read.
async function readMp4Metadata(file) {
  const bytes = async (start, length) => new DataView(await file.slice(start, start + length).arrayBuffer())
  let offset = 0
  let moov = null
  while (offset + 8 <= file.size) {
    const head = await bytes(offset, 16)
    let size = head.getUint32(0)
    const type = String.fromCharCode(head.getUint8(4), head.getUint8(5), head.getUint8(6), head.getUint8(7))
    let headerSize = 8
    if (size === 1) {
      size = Number(head.getBigUint64(8))
      headerSize = 16
    } else if (size === 0) {
      size = file.size - offset
    }
    if (size < headerSize) break
    if (type === 'moov') {
      if (size > 64 << 20) break
      moov = { view: await bytes(offset + headerSize, size - headerSize) }
      break
    }
    offset += size
  }
  if (!moov) return {}
  const out = {}
  const view = moov.view
  const children = (start, end, visit) => {
    let p = start
    while (p + 8 <= end) {
      const size = view.getUint32(p)
      const type = String.fromCharCode(view.getUint8(p + 4), view.getUint8(p + 5), view.getUint8(p + 6), view.getUint8(p + 7))
      if (size < 8 || p + size > end) break
      visit(type, p + 8, p + size)
      p += size
    }
  }
  children(0, view.byteLength, (type, start, end) => {
    if (type === 'mvhd') {
      const version = view.getUint8(start)
      const created = version === 1 ? Number(view.getBigUint64(start + 4)) : view.getUint32(start + 4)
      const timescale = view.getUint32(start + (version === 1 ? 20 : 12))
      const duration = version === 1 ? Number(view.getBigUint64(start + 24)) : view.getUint32(start + 16)
      if (timescale > 0) out.durationMs = Math.round((duration / timescale) * 1000)
      // Phone cameras stamp the creation time when recording stops (checked 2026-10-05 on the
      // G7 and the Pixel: a clip named 16:25:11 was stamped 16:25:20, 8.3 s long), so the taken
      // time, when recording started, is that minus the length. Migration 00012 did the same to
      // the videos uploaded before.
      if (created > 2082844800) out.takenAt = new Date((created - 2082844800) * 1000 - (out.durationMs ?? 0)).toISOString()
    }
    // The video track's frame size (tkhd's last 8 bytes, 16.16 fixed point), turned upright by
    // its matrix (a phone's portrait clip is stored landscape with a 90° rotation).
    if (type === 'trak' && out.width == null) {
      let size = null
      let isVideo = false
      children(start, end, (inner, s, e) => {
        if (inner === 'tkhd' && e - s >= 84) {
          const w = view.getUint32(e - 8) / 65536
          const h = view.getUint32(e - 4) / 65536
          const a = view.getInt32(e - 44)
          if (w > 0 && h > 0) size = a === 0 ? [h, w] : [w, h]
        }
        if (inner === 'mdia') {
          children(s, e, (m, ms) => {
            if (m === 'hdlr') isVideo = String.fromCharCode(view.getUint8(ms + 8), view.getUint8(ms + 9), view.getUint8(ms + 10), view.getUint8(ms + 11)) === 'vide'
          })
        }
      })
      if (isVideo && size) [out.width, out.height] = size.map(Math.round)
    }
    if (type === 'udta') {
      children(start, end, (inner, s, e) => {
        if (inner !== '©xyz' || e - s < 5) return
        const text = new TextDecoder().decode(new Uint8Array(view.buffer, view.byteOffset + s + 4, e - s - 4))
        const m = text.match(/([+-]\d+(?:\.\d+)?)([+-]\d+(?:\.\d+)?)/)
        if (m) {
          out.latitude = Number(m[1])
          out.longitude = Number(m[2])
        }
      })
    }
  })
  return out
}

// PUTs body to a presigned link, reporting progress (0..1). A dropped connection or a storage
// error is retried twice, after 2 s and 6 s (phones lose signal mid-upload).
async function put(url, body, contentType, onProgress) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await putOnce(url, body, contentType, onProgress)
    } catch (err) {
      if (attempt >= 2 || !err.retry) throw err
      await new Promise((r) => setTimeout(r, attempt ? 6000 : 2000))
    }
  }
}

function putOnce(url, body, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded / e.total) }
    const fail = (message, retry) => reject(Object.assign(new Error(message), { retry }))
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : fail(`storage answered ${xhr.status}`, xhr.status >= 500))
    xhr.onerror = () => fail('the connection dropped', true)
    xhr.send(body)
  })
}

// Shown under a video this browser couldn't decode. It's still added (stored as recorded), but
// the site's player is the browser's own, so it won't play here, nor in other browsers without
// that codec. In practice it's HEVC, which phone cameras use by default (the Pixel's clips of
// 7 Oct 2026; desktop Chrome plays it only with hardware decoding).
export const VIDEO_FORMAT_WARNING =
  "This browser can't play this video's format (probably HEVC), so it may not play on the site. " +
  'Setting the camera to H.264 ("Most compatible") avoids this.'

// Adds one file to a walk. onStage(text) and onProgress(0..1) report how it's going. Resolves to
// the saved item plus warning: VIDEO_FORMAT_WARNING or null.
export async function uploadToWalk(walkId, file, { onStage, onProgress } = {}) {
  const contentType = contentTypeOf(file)
  const isVideo = contentType.startsWith('video/')
  if (!isVideo && !contentType.startsWith('image/')) throw new Error('not a photo or video')

  // Browsers don't load video in a hidden tab, so a video's preview waits until it's shown.
  if (isVideo && document.hidden) {
    onStage?.('Waiting for this tab')
    await new Promise((resolve) => {
      const check = () => { if (!document.hidden) { document.removeEventListener('visibilitychange', check); resolve() } }
      document.addEventListener('visibilitychange', check)
    })
  }
  onStage?.('Preparing')
  const info = isVideo ? await prepareVideo(file) : await preparePhoto(file)
  const links = await postJSON(`/api/walks/${walkId}/photos/uploads`, { content_type: contentType, size_bytes: file.size })

  // Progress counts bytes across the three PUTs (the previews are small).
  const total = file.size + (info.previews ? info.previews.thumb.size + info.previews.display.size : 0)
  let done = 0
  const step = (size) => (fraction) => onProgress?.((done + fraction * size) / total)
  onStage?.('Uploading')
  if (info.previews) {
    for (const name of ['thumb', 'display']) {
      const blob = info.previews[name]
      await put(links[`${name}_url`], blob, 'image/jpeg', step(blob.size))
      done += blob.size
    }
  }
  await put(links.original_url, file, contentType, step(file.size))
  onProgress?.(1)

  onStage?.('Saving')
  const saved = await postJSON(`/api/walks/${walkId}/photos`, {
    key: links.key,
    content_type: contentType,
    original_name: file.name,
    size_bytes: file.size,
    width: info.width ?? null,
    height: info.height ?? null,
    duration_ms: info.durationMs ?? null,
    taken_at: info.takenAt ?? null,
    latitude: info.latitude ?? null,
    longitude: info.longitude ?? null,
    has_preview: Boolean(info.previews),
  })
  return { ...saved, warning: isVideo && !info.previews ? VIDEO_FORMAT_WARNING : null }
}
